// variant-inventory.js - FIXED VERSION with proper event handling
class VariantInventory {
  constructor() {
    this.variants = [];
    this.currentVariantId = null;
    this.initialized = false;
    
    // Use event delegation to handle dynamic changes
    this.setupEventDelegation();
    
    // Initialize when ready
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this.init());
    } else {
      setTimeout(() => this.init(), 1000);
    }
  }
  
  init() {
    console.log('🎯 Inventory system initializing...');
    
    // Load variant data
    this.loadVariantData();
    
    // Setup mutation observer for DOM changes
    this.setupMutationObserver();
    
    // Initial update
    this.updateOnLoad();

    // Also update any cart/drawer inventory badges on first load
    this.updateCartInventoryBadges();
    
    this.initialized = true;
    console.log('✅ Inventory system ready');
  }
  
  loadVariantData() {
    const jsonScript = document.getElementById('ProductVariantsJson');
    if (!jsonScript) {
      console.error('❌ ProductVariantsJson not found');
      return;
    }
    
    try {
      const data = JSON.parse(jsonScript.textContent);
      this.variants = data.variants;
      console.log(`✅ Loaded ${this.variants.length} variants`);
    } catch (error) {
      console.error('❌ Error parsing variant data:', error);
    }
  }
  
  setupEventDelegation() {
    console.log('🎯 Setting up event delegation...');
    
    // Use event delegation for ALL clicks on the document
    document.addEventListener('click', (event) => {
      this.handleClick(event);
    });
    
    // Also listen for change events (for radio buttons)
    document.addEventListener('change', (event) => {
      this.handleChange(event);
    });

    // Update cart badges on typical cart events (theme-dependent)
    document.addEventListener('cart:updated', () => this.updateCartInventoryBadges());
    document.addEventListener('cart:refresh', () => this.updateCartInventoryBadges());
    document.addEventListener('ajaxCart:render', () => this.updateCartInventoryBadges());
  }
  
  setupMutationObserver() {
    // Watch for DOM changes that might indicate variant selection
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        // Check if variant selection classes changed
        if (mutation.type === 'attributes' && 
            mutation.attributeName === 'class' &&
            mutation.target.classList.contains('variant_option')) {
          
          // Wait a bit for theme to finish updating
          setTimeout(() => {
            this.checkAndUpdateVariant();
          }, 200);
        }
        
        // Check if new elements were added
        if (mutation.type === 'childList') {
          const addedRadios = mutation.addedNodes?.forEach(node => {
            if (node.nodeType === 1 && node.matches && 
                (node.matches('input.productOption') || node.querySelector('input.productOption'))) {
              console.log('New variant radio added, updating...');
              setTimeout(() => {
                this.checkAndUpdateVariant();
              }, 300);
            }
          });
        }
      });
    });
    
    // Start observing the variant selector area
    const variantContainer = document.querySelector('.product-option-selector-box, variant-selects');
    if (variantContainer) {
      observer.observe(variantContainer, {
        attributes: true,
        childList: true,
        subtree: true,
        attributeFilter: ['class', 'checked']
      });
      console.log('👀 Mutation observer started');
    }

    // Cart/drawer observer (for themes that re-render cart HTML)
    const cartRoot = document.querySelector('.minicart__outerbox, [data-cart-form], form#cart');
    if (cartRoot) {
      const cartObserver = new MutationObserver(() => {
        clearTimeout(this._cartInvT);
        this._cartInvT = setTimeout(() => this.updateCartInventoryBadges(), 150);
      });
      cartObserver.observe(cartRoot, { childList: true, subtree: true });
      console.log('🛒 Cart inventory observer started');
    }

  }
  
  handleClick(event) {
    // Check if click is on a variant option
    const variantOption = event.target.closest('.variant_option');
    if (variantOption) {
      console.log('🎯 Click on variant option');
      
      // Wait a moment for theme to update the radio button
      setTimeout(() => {
        this.checkAndUpdateVariant();
      }, 100);
      return;
    }
    
    // Check if click is directly on a radio button
    if (event.target.matches('input.productOption[type="radio"]')) {
      console.log('🎯 Click on radio button');
      setTimeout(() => {
        this.handleVariantSelection(event.target);
      }, 50);
    }
  }
  
  handleChange(event) {
    // Handle radio button change events
    if (event.target.matches('input.productOption[type="radio"]')) {
      console.log('🔄 Radio button changed');
      setTimeout(() => {
        this.handleVariantSelection(event.target);
      }, 50);
    }
  }
  
  handleVariantSelection(radioElement) {
    const variantId = this.getVariantIdFromElement(radioElement);
    if (variantId) {
      console.log(`🔄 Selected variant ${variantId}: ${radioElement.value}`);
      this.updateInventory(variantId);
    }
  }
  
  checkAndUpdateVariant() {
    // Find the currently selected variant
    const variantId = this.getCurrentVariantId();
    if (variantId && variantId !== this.currentVariantId) {
      console.log(`🔄 Auto-detected variant change to ${variantId}`);
      this.updateInventory(variantId);
    }
  }
  
  getVariantIdFromElement(element) {
    if (!element) return null;
    
    // Try to get variant ID from the element itself
    if (element.getAttribute('data-variant-id')) {
      return parseInt(element.getAttribute('data-variant-id'));
    }
    
    // If it's a container, find the radio inside
    const radio = element.querySelector('input.productOption[data-variant-id]');
    if (radio) {
      return parseInt(radio.getAttribute('data-variant-id'));
    }
    
    return null;
  }
  
  getCurrentVariantId() {
    // Method 1: Check selected radio button
    const selectedRadio = document.querySelector('input.productOption[type="radio"]:checked');
    if (selectedRadio) {
      const variantId = selectedRadio.getAttribute('data-variant-id');
      if (variantId) return parseInt(variantId);
    }
    
    // Method 2: Check element with selected class
    const selectedOption = document.querySelector('.variant_option.selected');
    if (selectedOption) {
      const radio = selectedOption.querySelector('input.productOption');
      if (radio) {
        const variantId = radio.getAttribute('data-variant-id');
        if (variantId) return parseInt(variantId);
      }
    }
    
    // Method 3: Check JSON script (fallback)
    const jsonScript = document.querySelector('script[type="application/json"][data-selected-variant]');
    if (jsonScript) {
      try {
        const data = JSON.parse(jsonScript.textContent);
        if (data.id) return data.id;
      } catch(e) {}
    }
    
    return null;
  }
  
  updateOnLoad() {
    const variantId = this.getCurrentVariantId();
    console.log('📄 Initial variant ID:', variantId);
    
    if (variantId) {
      this.updateInventory(variantId);
    } else if (this.variants.length > 0) {
      // Fallback to first variant
      this.updateInventory(this.variants[0].id);
    }
  }
  
  updateInventory(variantId) {
    // Prevent unnecessary updates
    if (variantId === this.currentVariantId) {
      console.log(`⏭️ Same variant ${variantId}, skipping update`);
      return;
    }
    
    this.currentVariantId = variantId;
    
    const variant = this.variants.find(v => v.id === variantId);
    if (!variant) {
      console.error(`❌ Variant ${variantId} not found`);
      this.showError(`バリアント ${variantId} が見つかりません`);
      return;
    }
    
    console.log(`📊 Updating inventory for variant ${variantId}:`, {
      quantity: variant.inventory_quantity,
      policy: variant.inventory_policy,
      available: variant.available
    });
    
    this.renderInventory(variant);
  }
  
  // In your JavaScript, update the renderInventory function:
  renderInventory(variant) {
    const container = document.getElementById('variant-inventory');
    if (!container) return;
    
    const qty = variant.inventory_quantity;
    const available = variant.available;
    const policy = variant.inventory_policy;
    
    let html = '';
    let color = '';
    let bgColor = '';
    let borderColor = '';
    let text = '';
    
    if (available) {
      if (qty > 10) {
        text = '在庫あり';
        color = '#10b981';
        bgColor = 'rgba(16, 185, 129, 0.1)';
        borderColor = '#10b981';
      } else if (qty > 0) {
        if (policy === 'continue') {
          text = `残り ${qty} 点（追加分はご予約受付中）`;
          color = '#8b5cf6';
          bgColor = 'rgba(139, 92, 246, 0.1)';
          borderColor = '#8b5cf6';
        } else {
          text = `残り ${qty} 点です`;
          color = '#f59e0b';
          bgColor = 'rgba(245, 158, 11, 0.1)';
          borderColor = '#f59e0b';
        }
      } else {
        if (policy === 'continue') {
          text = '予約受付中';
          color = '#3b82f6';
          bgColor = 'rgba(59, 130, 246, 0.1)';
          borderColor = '#3b82f6';
        } else {
          text = '在庫切れ';
          color = '#ef4444';
          bgColor = 'rgba(239, 68, 68, 0.1)';
          borderColor = '#ef4444';
        }
      }
    } else {
      text = '在庫切れ';
      color = '#ef4444';
      bgColor = 'rgba(239, 68, 68, 0.1)';
      borderColor = '#ef4444';
    }
    
    // Apply inline styles to avoid CSS conflicts
    container.innerHTML = `
      <p class="product-stock" style="
        font-size: 16px;
        font-weight: 500;
        margin: 0;
        padding: 8px 12px;
        border-radius: 6px;
        color: ${color};
        background: ${bgColor};
        border-left: 4px solid ${borderColor};
      ">
        ${text}
      </p>
    `;
  }
  
  
  /**
   * Compute inventory display (text + className) for a variant-like object.
   * Accepts objects from ProductVariantsJson or /variants/{id}.js
   */
  computeInventoryDisplay(variant) {
    const qty = Number(variant.inventory_quantity ?? 0);
    const available = Boolean(variant.available);
    const policy = variant.inventory_policy || 'deny';

    let text = '';
    let className = '';

    if (available) {
      if (qty > 10) {
        text = '在庫あり';
        className = 'in';
      } else if (qty > 0) {
        if (policy === 'continue') {
          text = `残り ${qty} 点（追加分はご予約受付中）`;
          className = 'preorder-available';
        } else {
          text = `残り ${qty} 点です`;
          className = 'low';
        }
      } else {
        if (policy === 'continue') {
          text = '予約受付中';
          className = 'preorder';
        } else {
          text = '在庫切れ';
          className = 'out';
        }
      }
    } else {
      text = '在庫切れ';
      className = 'out';
    }

    return { text, className };
  }

  /**
   * Update inventory badges rendered in cart page / cart drawer.
   * Looks for elements with [data-variant-inventory] and refreshes from /variants/{id}.js
   */
  async updateCartInventoryBadges() {
    const els = document.querySelectorAll('[data-variant-inventory][data-variant-id]');
    if (!els.length) return;

    // Small in-memory cache for this page view
    this._variantCache = this._variantCache || new Map();

    const fetchVariant = async (id) => {
      if (this._variantCache.has(id)) return this._variantCache.get(id);

      const p = fetch(`/variants/${id}.js`, { credentials: 'same-origin' })
        .then(r => (r.ok ? r.json() : null))
        .catch(() => null);

      this._variantCache.set(id, p);
      return p;
    };

    await Promise.all(Array.from(els).map(async (el) => {
      const id = el.getAttribute('data-variant-id');
      if (!id) return;

      const variant = await fetchVariant(id);
      if (!variant) return;

      // IMPORTANT:
      // Shopify's /variants/{id}.js often does NOT include inventory_quantity on storefronts,
      // which would make qty default to 0 and incorrectly overwrite Liquid-rendered badges as "在庫切れ".
      // If inventory_quantity is missing, keep the existing Liquid text and do not overwrite.
      if (variant.inventory_quantity === undefined || variant.inventory_quantity === null) {
        // Optional: you can still reflect true sold-out status if Shopify says available=false.
        if (variant.available === false) {
          el.textContent = '在庫切れ';
          el.classList.add('product-stock');
          el.classList.forEach((c) => { if (c.startsWith('product-stock--')) el.classList.remove(c); });
          el.classList.add('product-stock--out');
        }
        return;
      }

      const { text, className } = this.computeInventoryDisplay(variant);

      el.textContent = text;

      // Ensure base class exists + normalize modifier
      el.classList.add('product-stock');
      el.classList.forEach((c) => {
        if (c.startsWith('product-stock--')) el.classList.remove(c);
      });
      el.classList.add(`product-stock--${className}`);
    }));
  }

  generateInventoryHTML(variant) {
    const qty = variant.inventory_quantity;
    const available = variant.available;
    const policy = variant.inventory_policy;
    
    let html = '';
    let className = '';
    
    if (available) {
      if (qty > 10) {
        html = '在庫あり';
        className = 'in';
      } else if (qty > 0) {
        if (policy === 'continue') {
          html = `残り ${qty} 点（追加分はご予約受付中）`;
          className = 'low preorder-available';
        } else {
          html = `残り ${qty} 点です`;
          className = 'low';
        }
      } else {
        if (policy === 'continue') {
          html = '予約受付中';
          className = 'preorder';
        } else {
          html = '在庫切れ';
          className = 'out';
        }
      }
    } else {
      html = '在庫切れ';
      className = 'out';
    }
    
    return `<p class="product-stock product-stock--${className}">${html}</p>`;
  }
  
  showError(message) {
    const container = document.getElementById('variant-inventory');
    if (container) {
      container.innerHTML = `<p class="product-stock product-stock--error">${message}</p>`;
    }
  }
  
  // Debug method
  debug() {
    console.log('=== INVENTORY DEBUG ===');
    console.log('Current variant ID:', this.currentVariantId);
    console.log('Total variants:', this.variants.length);
    console.log('Variants:', this.variants);
    
    // Check all radios
    const radios = document.querySelectorAll('input.productOption');
    console.log(`Found ${radios.length} radio buttons:`);
    radios.forEach((radio, i) => {
      console.log(`  ${i}: ${radio.value}`, {
        id: radio.getAttribute('data-variant-id'),
        checked: radio.checked,
        parentClass: radio.closest('.variant_option')?.className
      });
    });
    
    // Check selected
    const selectedId = this.getCurrentVariantId();
    console.log('Currently selected variant ID:', selectedId);
  }
}

// Initialize
window.variantInventory = new VariantInventory();

// Debug helper
window.debugInventory = () => {
  if (window.variantInventory) {
    window.variantInventory.debug();
  } else {
    console.error('Inventory system not initialized');
  }
};

(function () {
  // Legacy preorder-only gating removed: we now use unified gating (Terms + Preorder)
  function syncAckState() {
    if (window.__tfkAckEvaluate) window.__tfkAckEvaluate();
  }

  document.addEventListener('DOMContentLoaded', syncAckState);

  // Re-run when cart/drawer HTML changes (AJAX re-render)
  const mo = new MutationObserver(() => syncAckState());
  mo.observe(document.body, { childList: true, subtree: true });
})();

(function () {
  async function fetchCart() {
    const res = await fetch('/cart.js', { credentials: 'same-origin' });
    if (!res.ok) throw new Error('Failed to fetch cart.js');
    return res.json();
  }

  function calcPreorderCount(cart) {
    // “予約受付中” condition:
    // available && inventory_policy == 'continue' && inventory_quantity <= 0
    // In cart.js, inventory_quantity is often present; if not, treat missing as NOT preorder
    let count = 0;

    (cart.items || []).forEach((item) => {
      const v = item.variant || item; // some themes flatten fields
      const available = v.available === true || item.available === true;

      const inventoryPolicy =
        (v.inventory_policy || item.inventory_policy || '').toString();

      // If inventory_quantity is missing, DON'T assume 0 (that would create false preorder).
      const hasQty =
        typeof v.inventory_quantity === 'number' ||
        typeof item.inventory_quantity === 'number';

      const qty =
        (typeof v.inventory_quantity === 'number' ? v.inventory_quantity :
         typeof item.inventory_quantity === 'number' ? item.inventory_quantity :
         null);

      const isPreorder = available && inventoryPolicy === 'continue' && hasQty && qty <= 0;

      if (isPreorder) count += (item.quantity || 0);
    });

    return count;
  }

  function setDrawerAckUI(preorderCount) {
    document.querySelectorAll('[data-preorder-ack-container]').forEach((box) => {
      const checkbox = box.querySelector('[data-preorder-ack-checkbox]');
      const text = box.querySelector('[data-preorder-ack-text]');

      if (preorderCount <= 0) {
        // Hide + reset
        box.style.display = 'none';
        box.dataset.hasPreorder = '0';
        if (checkbox) checkbox.checked = false;
        if (window.__tfkAckEvaluate) window.__tfkAckEvaluate();
        return;
      }

      // Show + update count in text
      box.style.display = '';
      box.dataset.hasPreorder = '1';
      if (window.__tfkAckEvaluate) window.__tfkAckEvaluate();
      if (text) {
        text.innerHTML =
          `このカートには${preorderCount}点の予約商品が含まれております。<br>` +
          `予約商品のお届けには、約2週間のお時間を頂戴する場合がございます。<br>` +
          `なお、ご注文後の予約商品は、お客様のご都合によるキャンセルをお受けいたしかねます。`;
      }

      // Require checkbox
      const applyDisabled = () => {
        const ok = checkbox && checkbox.checked;
        checkoutButtons.forEach((btn) => (btn.disabled = !ok));
      };

      if (checkbox && !checkbox.dataset.bound) {
        checkbox.dataset.bound = '1';
        checkbox.addEventListener('change', applyDisabled);
      }
      applyDisabled();
    });
  }

  
  // Prefer DOM-based preorder counting (reliable on Shopify storefront; /cart.js often omits inventory fields)
  function calcPreorderCountFromDOM() {
    let total = 0;

    // “予約受付中” badge is rendered as: .product-stock--preorder
    const badges = document.querySelectorAll('.product-stock--preorder');

    badges.forEach((badge) => {
      const row =
        badge.closest('.cart-item, .ajax-cart__product, .minicart__item, li, .cart__item') ||
        badge.parentElement;

      let qty = 1;
      if (row) {
        const qtyInput =
          row.querySelector('input[name="updates[]"]') ||
          row.querySelector('input.quantity-input') ||
          row.querySelector('input[type="number"]');

        if (qtyInput) {
          const v = parseInt(qtyInput.value, 10);
          if (!Number.isNaN(v) && v > 0) qty = v;
        }
      }

      total += qty;
    });

    return total;
  }

async function refreshPreorderAck() {
    try {
      // 1) DOM-based count (fast + reliable)
      const domCount = calcPreorderCountFromDOM();
      if (domCount > 0) {
        setDrawerAckUI(domCount);
        return;
      }

      // 2) Fallback to /cart.js (some themes expose inventory fields)
      const cart = await fetchCart();
      const count = calcPreorderCount(cart);
      setDrawerAckUI(count);
    } catch (e) {
      // If cart.js fails, don’t break checkout
      console.warn(e);
    }
  }

  // Run on load
  document.addEventListener('DOMContentLoaded', refreshPreorderAck);

  // Re-run whenever the drawer DOM changes (common with AJAX drawers)
  const mo = new MutationObserver(() => {
    // debounce-ish
    clearTimeout(window.__preorderAckTO);
    window.__preorderAckTO = setTimeout(refreshPreorderAck, 80);
  });
  mo.observe(document.body, { childList: true, subtree: true });

  // Also run after any click inside cart drawer that might change cart
  document.addEventListener('click', (e) => {
    const inDrawer = e.target.closest('.cart-drawer, .minicart__outerbox, [data-ajax-cart]');
    if (!inDrawer) return;
    // Give theme scripts time to update cart, then refresh
    setTimeout(refreshPreorderAck, 250);
    setTimeout(refreshPreorderAck, 800);
  });
})();

/* ToyFuku: enforce Terms + Preorder acknowledgements before checkout */
(function () {
  function isVisible(el) {
    if (!el) return false;
    if (el.style && el.style.display === 'none') return false;
    return !!(el.offsetWidth || el.offsetHeight || el.getClientRects().length);
  }

  function getCheckoutButtons(scope) {
    return Array.from(
      (scope || document).querySelectorAll(
        'button[name="checkout"], #cartCheckout, [data-cart-checkout-buttons] button, .ajax-cart__checkout button'
      )
    );
  }

  function evaluate(scope) {
    const root = scope || document;

    // Terms checkbox exists only when cart has items (Liquid condition)
    const terms = root.querySelector('[data-terms-ack-checkbox]');
    const termsOk = !terms || terms.checked;

    // Preorder checkbox required only when a preorder container is visible.
    // Some themes render BOTH cart page + drawer markup at once; handle all of them safely.
    const preorderBoxes = Array.from(root.querySelectorAll('[data-preorder-ack-container]'));
    let preorderOk = true;

    preorderBoxes.forEach((box) => {
      if (!box) return;
      const required = isVisible(box);
      if (!required) return;

      const cb = box.querySelector('[data-preorder-ack-checkbox]');
      if (!cb || !cb.checked) preorderOk = false;
    });

    const ok = termsOk && preorderOk;

    getCheckoutButtons(root).forEach((btn) => {
      btn.disabled = !ok;
      btn.classList.toggle('is-disabled-by-ack', !ok);
    });
  }

  function bindOnce() {
    document.querySelectorAll('[data-terms-ack-checkbox], [data-preorder-ack-checkbox]').forEach((cb) => {
      if (cb.dataset.ackBound) return;
      cb.dataset.ackBound = '1';
      cb.addEventListener('change', () => evaluate());
    });
    window.__tfkAckEvaluate = evaluate;
    evaluate();
  }

  document.addEventListener('DOMContentLoaded', bindOnce);

  const mo = new MutationObserver(() => {
    // New cart drawer HTML often re-renders checkboxes
    bindOnce();
  });
  mo.observe(document.body, { childList: true, subtree: true });
})();
